import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, Pipette } from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────

function hexToRgb(hex: string): [number, number, number] | null {
    const clean = hex.replace("#", "");
    if (clean.length === 3) {
        const r = parseInt(clean[0]! + clean[0]!, 16);
        const g = parseInt(clean[1]! + clean[1]!, 16);
        const b = parseInt(clean[2]! + clean[2]!, 16);
        return [r, g, b];
    }
    if (clean.length === 6) {
        const r = parseInt(clean.slice(0, 2), 16);
        const g = parseInt(clean.slice(2, 4), 16);
        const b = parseInt(clean.slice(4, 6), 16);
        if ([r, g, b].some(isNaN)) return null;
        return [r, g, b];
    }
    return null;
}

function rgbToHex(r: number, g: number, b: number): string {
    return (
        "#" +
        [r, g, b]
            .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0"))
            .join("")
    );
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return [0, 0, Math.round(l * 100)];
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
    return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    h /= 360; s /= 100; l /= 100;
    if (s === 0) { const v = Math.round(l * 255); return [v, v, v]; }
    const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1; if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [
        Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        Math.round(hue2rgb(p, q, h) * 255),
        Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    ];
}

// ── Component ────────────────────────────────────────────────

export function ColorConverter() {
    const [hex, setHex] = useState("#a78bfa");
    const [rgb, setRgb] = useState({ r: 167, g: 139, b: 250 });
    const [hsl, setHsl] = useState({ h: 258, s: 94, l: 76 });
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        document.title = "Color Converter — Hex, RGB, HSL — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute("content",
                "Convert between Hex, RGB, and HSL color formats. Visual color picker with Tailwind CSS class output. Free online tool.");
        }
    }, []);

    const updateFromHex = (h: string) => {
        setHex(h);
        const result = hexToRgb(h);
        if (result) {
            const [r, g, b] = result;
            setRgb({ r, g, b });
            const [hh, ss, ll] = rgbToHsl(r, g, b);
            setHsl({ h: hh, s: ss, l: ll });
        }
    };

    const updateFromRgb = (r: number, g: number, b: number) => {
        setRgb({ r, g, b });
        setHex(rgbToHex(r, g, b));
        const [hh, ss, ll] = rgbToHsl(r, g, b);
        setHsl({ h: hh, s: ss, l: ll });
    };

    const updateFromHsl = (h: number, s: number, l: number) => {
        setHsl({ h, s, l });
        const [r, g, b] = hslToRgb(h, s, l);
        setRgb({ r, g, b });
        setHex(rgbToHex(r, g, b));
    };

    const formats = useMemo(() => ({
        hex: hex.toUpperCase(),
        rgb: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
        hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`,
        tailwind: `[${hex}]`,
    }), [hex, rgb, hsl]);

    const copy = async (val: string, key: string) => {
        await navigator.clipboard.writeText(val);
        setCopied(key);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link to="/" className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors">ballah code</Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">tools</Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">home</Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">Tools</Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Color Converter</span>
                </div>

                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">Color Converter</h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Convert between Hex, RGB, and HSL color formats. Use the sliders or type values directly.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Color preview + picker */}
                    <div>
                        <div
                            className="w-full aspect-[2/1] rounded-xl border border-white/[0.06] mb-4 flex items-end p-4"
                            style={{ backgroundColor: hex }}
                        >
                            <span className="text-[13px] font-mono px-2 py-1 rounded bg-black/40 text-white/80 backdrop-blur-sm">
                                {hex}
                            </span>
                        </div>

                        {/* Native color picker */}
                        <div className="flex items-center gap-3 mb-6">
                            <label className="flex items-center gap-2 text-[13px] text-zinc-500 cursor-pointer">
                                <Pipette size={14} />
                                <input
                                    type="color"
                                    value={hex}
                                    onChange={(e) => updateFromHex(e.target.value)}
                                    className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                                />
                                Pick color
                            </label>
                        </div>

                        {/* Output formats */}
                        <div className="bg-[#111113] border border-white/[0.06] rounded-xl overflow-hidden">
                            {Object.entries(formats).map(([key, val], i) => (
                                <div key={key} className={`flex items-center justify-between px-4 py-3 text-[13px] ${i < Object.keys(formats).length - 1 ? "border-b border-white/[0.04]" : ""}`}>
                                    <span className="text-zinc-600 uppercase text-[11px] font-mono tracking-wider">{key}</span>
                                    <div className="flex items-center gap-2">
                                        <code className="text-zinc-300 font-mono">{val}</code>
                                        <button onClick={() => copy(val, key)} className="text-zinc-700 hover:text-zinc-300 transition-colors">
                                            {copied === key ? <Check size={11} className="text-green-400" /> : <Copy size={11} />}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sliders */}
                    <div className="space-y-6">
                        {/* Hex input */}
                        <div>
                            <label className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider block mb-2">Hex</label>
                            <input
                                value={hex}
                                onChange={(e) => updateFromHex(e.target.value)}
                                className="w-full bg-[#111113] border border-white/[0.06] rounded-lg px-4 py-2.5 text-[14px] text-zinc-300 font-mono outline-none focus:border-white/[0.1] transition-colors"
                            />
                        </div>

                        {/* RGB sliders */}
                        {(["r", "g", "b"] as const).map((ch) => (
                            <div key={ch}>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                        {ch === "r" ? "Red" : ch === "g" ? "Green" : "Blue"}
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={255}
                                        value={rgb[ch]}
                                        onChange={(e) => updateFromRgb(
                                            ch === "r" ? parseInt(e.target.value) || 0 : rgb.r,
                                            ch === "g" ? parseInt(e.target.value) || 0 : rgb.g,
                                            ch === "b" ? parseInt(e.target.value) || 0 : rgb.b,
                                        )}
                                        className="w-16 bg-[#111113] border border-white/[0.06] rounded px-2 py-1 text-[12px] text-zinc-300 font-mono outline-none text-center"
                                    />
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={255}
                                    value={rgb[ch]}
                                    onChange={(e) => updateFromRgb(
                                        ch === "r" ? parseInt(e.target.value) : rgb.r,
                                        ch === "g" ? parseInt(e.target.value) : rgb.g,
                                        ch === "b" ? parseInt(e.target.value) : rgb.b,
                                    )}
                                    className="w-full accent-violet-400"
                                />
                            </div>
                        ))}

                        {/* HSL sliders */}
                        {(["h", "s", "l"] as const).map((ch) => (
                            <div key={ch}>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                        {ch === "h" ? "Hue" : ch === "s" ? "Saturation" : "Lightness"}
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        max={ch === "h" ? 360 : 100}
                                        value={hsl[ch]}
                                        onChange={(e) => updateFromHsl(
                                            ch === "h" ? parseInt(e.target.value) || 0 : hsl.h,
                                            ch === "s" ? parseInt(e.target.value) || 0 : hsl.s,
                                            ch === "l" ? parseInt(e.target.value) || 0 : hsl.l,
                                        )}
                                        className="w-16 bg-[#111113] border border-white/[0.06] rounded px-2 py-1 text-[12px] text-zinc-300 font-mono outline-none text-center"
                                    />
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={ch === "h" ? 360 : 100}
                                    value={hsl[ch]}
                                    onChange={(e) => updateFromHsl(
                                        ch === "h" ? parseInt(e.target.value) : hsl.h,
                                        ch === "s" ? parseInt(e.target.value) : hsl.s,
                                        ch === "l" ? parseInt(e.target.value) : hsl.l,
                                    )}
                                    className="w-full accent-violet-400"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">About color formats</h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>Hex, RGB, and HSL are the three most common ways to represent colors in web development. Hex uses a # followed by 6 hex digits. RGB specifies red, green, and blue channels from 0-255. HSL uses hue (0-360°), saturation (0-100%), and lightness (0-100%).</p>
                        <p>HSL is often the most intuitive for designers because you can adjust a color's brightness or saturation without changing its hue. Tailwind CSS class output lets you use custom colors with bracket notation.</p>
                        <p>Built by the team behind <Link to="/" className="text-violet-400 hover:text-violet-300 transition-colors">Ballah Code</Link>.</p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/regex-tester" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Regex Tester</Link>
                        <Link to="/tools/json-formatter" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JSON Formatter</Link>
                        <Link to="/tools/diff-checker" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Diff Checker</Link>
                    </div>
                </section>
            </main>

            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">ballah code</Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">tools</Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
